'use client';


import { type FC } from 'react';
import type { WeaponClass } from '@cs-shot/battle-sim';
import { cn } from '../../../../utils/cn';
import { rarityColor, stockSrc } from './model';
import { LockIcon, LOCKED_HINT } from './shared';
import { WEAPON_CLASSES, isWorn, type RailEntry } from './weaponRail';

export interface WeaponSkinSectionProps {
  rail: RailEntry[];
  selection: Record<string, string>;
  preview: WeaponClass;
  saveState: 'idle' | 'saving' | 'saved' | 'error';
  onPick: (entry: RailEntry) => void;
}

export const WeaponSkinSection: FC<WeaponSkinSectionProps> = ({ rail, selection, preview, saveState, onPick }) => {
  const anyLocked = rail.some((e) => e.skin && !e.skin.owned);
  const nearest = rail
    .map((e) => e.skin?.eloToUnlock)
    .filter((n): n is number => typeof n === 'number')
    .sort((a, b) => a - b)[0];

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-medium text-label-secondary">Оружие</div>
        <span className={cn('text-[11px]', saveState === 'error' ? 'text-accent-red' : 'text-label-secondary')}>
          {saveState === 'saving' ? 'Сохраняю…'
            : saveState === 'saved' ? 'Сохранено'
              : saveState === 'error' ? 'Не сохранилось — попробуй ещё раз'
                : 'на урон не влияет'}
        </span>
      </div>

      <div className="mt-2 flex flex-col gap-4">
        {WEAPON_CLASSES.map(({ weapon, label }) => {
          const items = rail.filter((e) => e.weapon === weapon);
          const worn = items.find((e) => isWorn(e, selection));
          return (
            <div key={weapon}>
              <div className="flex items-baseline gap-2">
                <div className="text-sm font-semibold text-label-primary">{label}</div>

                {worn?.skin && <span className="text-[11px] text-label-secondary">{worn.skin.title}</span>}
                {weapon === preview && <span className="h-1.5 w-1.5 rounded-full bg-accent-blue" />}
              </div>
              {items.length <= 1 ? (
                <p className="mt-1.5 text-xs text-label-secondary">Скинов пока нет</p>
              ) : (
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {items.map((e) => (
                    <Tile key={`${e.weaponKey}-${e.skin?.name ?? 'stock'}`} entry={e}
                      active={isWorn(e, selection)}
                      onClick={() => onPick(e)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {anyLocked && (
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-label-secondary">
          <LockIcon size={11} className="text-side-t" />
          {typeof nearest === 'number' ? `Ближайший скин откроется через ${nearest} рейтинга` : LOCKED_HINT}
        </p>
      )}
    </div>
  );
};

const Tile: FC<{ entry: RailEntry; active: boolean; onClick: () => void }> = ({ entry, active, onClick }) => {
  const locked = entry.skin ? !entry.skin.owned : false;
  return (
    <button onClick={() => !locked && onClick()} aria-disabled={locked}
      className={cn('relative flex w-[104px] shrink-0 flex-col items-center gap-1 rounded-xl border p-2 transition-colors',
        active ? 'border-accent-blue bg-accent-blue/15' : 'border-white/10 bg-black/25')}
      style={active || locked || !entry.skin ? undefined : { borderColor: `${rarityColor(entry.skin.rarity)}55` }}>
      <img src={entry.skin?.spriteUrl ?? stockSrc(entry.weaponKey)} alt="" draggable={false}
        className={cn('object-contain', locked && 'opacity-45 grayscale')} style={{ width: 88, height: 35 }} />
      <span className={cn('w-full truncate text-center text-[11px] font-semibold', locked ? 'text-label-secondary' : 'text-label-primary')}>
        {entry.title}
      </span>
      {locked && (
        <span className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5">
          <LockIcon size={10} className="text-side-t" />
        </span>
      )}
    </button>
  );
};
